import { PlayerColor } from './types';

// ── Главный трек (52 клетки, по часовой стрелке) ──────────────────────────
// Стандартная раскладка доски Ludo 15×15.
// Убираем дубликат (последний элемент = [13,6] который уже есть как index 0)
// Правильный трек: ровно 52 уникальных клетки
export const TRACK: [number, number][] = (() => {
  const t: [number, number][] = [
    [13,6],[12,6],[11,6],[10,6],[9,6],
    [8,5],[8,4],[8,3],[8,2],[8,1],[8,0],
    [7,0],
    [6,0],[6,1],[6,2],[6,3],[6,4],[6,5],
    [5,6],[4,6],[3,6],[2,6],[1,6],
    [0,6],[0,7],
    [0,8],[1,8],[2,8],[3,8],[4,8],[5,8],
    [6,9],[6,10],[6,11],[6,12],[6,13],
    [6,14],[7,14],
    [8,14],[8,13],[8,12],[8,11],[8,10],[8,9],
    [9,8],[10,8],[11,8],[12,8],[13,8],
    [14,8],[14,7],
    [14,6],[13,7], // <-- эта клетка не входит в трек, это начало домашней колонки Red
  ];
  // Ровно 52 клетки основного трека (без домашних колонок)
  return t.slice(0, 52);
})();

// ── Домашние колонки (5 клеток, steps 52-56) ──────────────────────────────
// Последняя клетка прежней версии ([8,7],[6,7],[7,6],[7,8]) попадала внутрь
// центральной 3×3 зоны. Теперь 5 клеток, step 57 = CENTER отдельно.
export const HOME_COLS: Record<PlayerColor, [number, number][]> = {
  red:    [[13,7],[12,7],[11,7],[10,7],[9,7],[8,7]],
  green:  [[7,1],[7,2],[7,3],[7,4],[7,5],[7,6]],
  yellow: [[1,7],[2,7],[3,7],[4,7],[5,7],[6,7]],
  blue:   [[7,13],[7,12],[7,11],[7,10],[7,9],[7,8]],
};

// Центр (финиш)
export const CENTER: [number, number] = [7, 7];

// ── Смещения входа на трек по цветам ──────────────────────────────────────
// Каждый игрок начинает движение по треку с определённого индекса TRACK
export const TRACK_OFFSET: Record<PlayerColor, number> = {
  red:    0,
  green:  13,
  yellow: 26,
  blue:   39,
};

// ── Домашние слоты (начальное положение 4 фишек) ──────────────────────────
// Каждая домашняя зона — 6×6 клеток. Фишки на позициях +1 и +4 от угла,
// что даёт симметричный 2×2 квадрат ровно по центру зоны.
export const HOME_SLOTS: Record<PlayerColor, [number, number][]> = {
  red:    [[10,1],[10,4],[13,1],[13,4]],
  green:  [[1,1],[1,4],[4,1],[4,4]],
  yellow: [[1,10],[1,13],[4,10],[4,13]],
  blue:   [[10,10],[10,13],[13,10],[13,13]],
};

// ── Безопасные клетки (глобальные индексы на TRACK) ───────────────────────
// Стартовые клетки каждого цвета (offset 0) + звёздочки (offset 8)
export const SAFE_INDICES = new Set<number>([0, 8, 13, 21, 26, 34, 39, 47]);

// ── Конвертер steps → {row, col} ──────────────────────────────────────────
export function stepsToCoord(player: PlayerColor, steps: number): [number, number] {
  if (steps === -1) {
    // В доме — возвращаем центр домашней базы (используется только для анимации)
    return HOME_SLOTS[player][0];
  }
  if (steps >= 57) {
    return CENTER;
  }
  if (steps >= 51) {
    // Домашняя колонка (шаги 51–56)
    const homeIdx = steps - 51;
    return HOME_COLS[player][homeIdx] ?? CENTER;
  }
  // Главный трек (шаги 0–50)
  const idx = (TRACK_OFFSET[player] + steps) % 52;
  return TRACK[idx] ?? CENTER;
}

// ── Глобальный индекс на TRACK для фишки с данными steps ─────────────────
export function trackIndex(player: PlayerColor, steps: number): number {
  return (TRACK_OFFSET[player] + steps) % 52;
}

// ── Является ли клетка безопасной для данного игрока на steps ────────────
export function isSafeStep(player: PlayerColor, steps: number): boolean {
  if (steps < 0 || steps >= 51) return true; // дом и домашняя колонка всегда безопасны
  return SAFE_INDICES.has(trackIndex(player, steps));
}

// ── Обратный поиск: клетка → steps для данного игрока ─────────────────────
// Возвращает null если клетка не принадлежит ни одному валидному шагу игрока.
export function cellToSteps(player: PlayerColor, row: number, col: number): number | null {
  // Центр (финиш)
  if (row === CENTER[0] && col === CENTER[1]) return 57;

  // Домашняя колонка
  for (let i = 0; i < HOME_COLS[player].length; i++) {
    const [r, c] = HOME_COLS[player][i];
    if (r === row && c === col) return 51 + i;
  }

  // Главный трек
  for (let i = 0; i < TRACK.length; i++) {
    if (TRACK[i][0] === row && TRACK[i][1] === col) {
      return (i - TRACK_OFFSET[player] + 52) % 52;
    }
  }

  // Домашние слоты (вернуть фишку домой)
  for (const [r, c] of HOME_SLOTS[player]) {
    if (r === row && c === col) return -1;
  }

  return null;
}

// ── Порядок игроков ───────────────────────────────────────────────────────
export const PLAYER_ORDER: PlayerColor[] = ['red', 'green', 'yellow', 'blue'];

export function nextPlayer(current: PlayerColor): PlayerColor {
  const idx = PLAYER_ORDER.indexOf(current);
  return PLAYER_ORDER[(idx + 1) % 4];
}

